import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from 'src/components/Button';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import type { Metric } from 'src/utils/gql/types';
import type {
  KpiMetricTarget,
  KpiTargetDirection,
  KpiTargetPeriod,
} from '../types';
import {
  getKpiTargetDirectionLabel,
  KPI_TARGET_DIRECTION_OPTIONS,
  KPI_TARGET_PERIOD_OPTIONS,
  getKpiTargetPeriodLabel,
  parseTargetInput,
} from '../utils/targetUtils';
import palette from 'src/theme/palette';

// Stesso gradiente della sidebar menu Kipify (`SidebarNav` in `Drawer/NavSidebarDrawer/styled.ts`).
const KIPIFY_NAV_GRADIENT =
  'linear-gradient(to bottom, rgba(0, 45, 28, 1), rgba(3, 12, 22, 1))';

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: 22,
    width: '100%',
    maxWidth: 760,
    overflow: 'hidden',
    boxShadow: '0 18px 48px rgba(48, 54, 66, 0.12)',
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 3, 2.5),
  borderBottom: `1px solid ${palette.grey[120]}`,
  // Verde Kipify molto soft in testa alla modal (evita hex con alpha “appiccicata”).
  background:
    'linear-gradient(180deg, rgba(30, 237, 159, 0.12) 0%, rgba(30, 237, 159, 0) 100%)',
}));

const Panel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.25),
  borderRadius: 18,
  border: `1px solid ${palette.grey[120]}`,
  backgroundColor: palette.secondary.main,
}));

const PeriodGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
}));

const PeriodButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  minHeight: 44,
  borderRadius: 12,
  border: selected
    ? `1px solid ${palette.primary.main}`
    : `1px solid ${palette.grey[120]}`,
  background: selected ? palette.primary.light : palette.common.white,
  color: selected ? palette.text.primary : palette.text.secondary,
  fontWeight: 700,
  textTransform: 'none',
  justifyContent: 'flex-start',
  paddingInline: theme.spacing(1.5),
  '&:hover': {
    background: selected ? palette.primary.light : palette.grey[200],
  },
}));

interface MetricTargetModalProps {
  open: boolean;
  metric: Metric;
  target?: KpiMetricTarget | null;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars -- falso positivo sui nomi nei tipi callback.
  onSave: (nextTarget: KpiMetricTarget) => void;
  onDelete: () => void;
}

export const MetricTargetModal: React.FC<MetricTargetModalProps> = ({
  open,
  metric,
  target,
  onClose,
  onSave,
  onDelete,
}) => {
  const hasExistingTarget = Boolean(target);
  const [valueInput, setValueInput] = useState('');
  const [period, setPeriod] = useState<KpiTargetPeriod>('absolute');
  const [direction, setDirection] = useState<KpiTargetDirection>('at_or_above');

  useEffect(() => {
    if (!open) return;

    setValueInput(target?.value !== undefined ? String(target.value) : '');
    setPeriod(target?.period ?? 'absolute');
    setDirection(target?.direction ?? 'at_or_above');
  }, [open, target]);

  const parsedValue = useMemo(() => parseTargetInput(valueInput), [valueInput]);
  const hasError = valueInput.trim().length > 0 && parsedValue === null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false}>
      <Header>
        <Box>
          <Typography
            sx={{
              fontSize: { xs: '1.5rem', sm: '1.9rem' },
              lineHeight: 1.15,
              fontWeight: 700,
              color: palette.text.primary,
            }}
          >
            {hasExistingTarget
              ? `Edit target for ${metric.label}`
              : `Add a target for ${metric.label}`}
          </Typography>
          <Typography
            sx={{ mt: 0.75, color: palette.text.secondary, fontSize: '1rem' }}
          >
            We will automatically calculate if you&apos;re on track.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: palette.text.disabled }}>
          <CloseIcon />
        </IconButton>
      </Header>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.95fr' },
            gap: 2,
          }}
        >
          <Stack spacing={2}>
            <Panel>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: palette.text.primary,
                }}
              >
                Target value
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: palette.text.secondary,
                  fontSize: '0.92rem',
                }}
              >
                Insert the threshold you want this KPI to reach.
              </Typography>

              <TextField
                fullWidth
                placeholder="e.g. 10000"
                value={valueInput}
                onChange={(event) => setValueInput(event.target.value)}
                error={hasError}
                FormHelperTextProps={{
                  sx: {
                    mt: 0.75,
                    fontSize: '0.78rem',
                    color: palette.text.secondary,
                  },
                }}
                helperText={
                  hasError
                    ? 'Insert a valid numeric value.'
                    : 'Digits only; if you paste values with € or % we strip symbols automatically.'
                }
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    backgroundColor: palette.common.white,
                    paddingInline: 0,
                    '& fieldset': {
                      top: 0,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.grey[300],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary.main,
                      borderWidth: 1,
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.grey[120],
                  },
                }}
              />
            </Panel>

            <Panel>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: palette.text.primary,
                }}
              >
                Success criteria
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: palette.text.secondary,
                  fontSize: '0.92rem',
                }}
              >
                Success is measured against the{' '}
                {getKpiTargetPeriodLabel(period).toLowerCase()} target.
              </Typography>

              <TextField
                select
                fullWidth
                value={direction}
                onChange={(event) =>
                  setDirection(event.target.value as KpiTargetDirection)
                }
                SelectProps={{
                  displayEmpty: true,
                  variant: 'outlined',
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                    backgroundColor: palette.common.white,
                    paddingInline: 0,
                    '& fieldset': {
                      top: 0,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.grey[300],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: palette.primary.main,
                      borderWidth: 1,
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.grey[120],
                  },
                }}
              >
                {KPI_TARGET_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Panel>
          </Stack>

          <Stack spacing={2}>
            <Panel>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: palette.text.primary,
                }}
              >
                Target type
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: palette.text.secondary,
                  fontSize: '0.92rem',
                }}
              >
                Choose the time frame used to evaluate the target.
              </Typography>

              <PeriodGrid sx={{ mt: 2 }}>
                {KPI_TARGET_PERIOD_OPTIONS.map((option) => (
                  <PeriodButton
                    key={option.value}
                    selected={period === option.value}
                    onClick={() => setPeriod(option.value)}
                    startIcon={
                      option.value === 'absolute' ? (
                        <FlagOutlinedIcon />
                      ) : undefined
                    }
                  >
                    {option.label}
                  </PeriodButton>
                ))}
              </PeriodGrid>
            </Panel>

            <Panel>
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: palette.text.secondary,
                }}
              >
                Preview
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  fontSize: '1.15rem',
                  fontWeight: 700,
                  color: palette.text.primary,
                }}
              >
                {parsedValue !== null ? parsedValue : '...'}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: palette.text.secondary,
                  fontSize: '0.9rem',
                }}
              >
                {getKpiTargetDirectionLabel(direction)} on a{' '}
                {getKpiTargetPeriodLabel(period).toLowerCase()} basis.
              </Typography>
            </Panel>
          </Stack>
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          background: KIPIFY_NAV_GRADIENT,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {hasExistingTarget && (
            <Button
              type="button"
              variant="contained"
              color="error"
              onClick={onDelete}
              // Il theme globale `MuiButton.contained` forza il verde primario su tutti i contained:
              // qui serve rosso “delete” esplicito come nelle altre conferme distruttive.
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: palette.error.dark,
                  boxShadow: 'none',
                },
                '&:active': {
                  backgroundColor: palette.error.dark,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  backgroundColor: palette.grey[200],
                  color: palette.text.disabled,
                },
              }}
            >
              Delete target
            </Button>
          )}
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: 'rgba(30, 237, 159, 0.55)',
              color: 'rgba(255, 255, 255, 0.92)',
              '&:hover': {
                borderColor: palette.primary.main,
                backgroundColor: 'rgba(30, 237, 159, 0.12)',
              },
            }}
          >
            Cancel
          </Button>
        </Box>

        <Button
          type="button"
          variant="contained"
          onClick={() => {
            if (parsedValue === null) return;
            onSave({
              value: parsedValue,
              period,
              direction,
            });
          }}
          disabled={parsedValue === null}
        >
          Save target
        </Button>
      </Box>
    </StyledDialog>
  );
};
